import React, { useState } from "react";
import { useToast } from "../../store/ToastProvider";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { FeatureCard } from "../../components/common/FeatureCard";
import { AI_FEATURE_ITEMS, AIFeatureItem } from "./aiFeatureConfig";
import { AIFeatureType } from "../../enums/CompassAI";
import {
  PDFExtractionModal,
  LinkageAnalysisModal,
  EngagementAssessmentModal,
} from "../../components/common/AIModals";
import {
  BackgroundContainer,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { GradientText } from "../../components/visuals/styledComponents";
import styled from "styled-components";
import { MESSAGES } from "../../messages";
import { greyText } from "../../styles";

const GradientTitle = styled(GradientText)`
  font-size: 24px;
  font-family: "ApercuBold";
  margin-left: 12px;
  padding-top: 0;
`;

// =============================================================================
// COMPONENT
// =============================================================================

export const CompassAI = (): JSX.Element => {
  const { addToast } = useToast();
  const [showSideMenu, setShowSideMenu] = useState<boolean>(false);

  // Modal states
  const [showPDFModal, setShowPDFModal] = useState<boolean>(false);
  const [showLinkageModal, setShowLinkageModal] = useState<boolean>(false);
  const [showAssessmentModal, setShowAssessmentModal] =
    useState<boolean>(false);

  const handleFeatureClick = (item: AIFeatureItem): void => {
    if (!item.isActive) {
      addToast(`${item.title} coming soon`, "info");
      return;
    }

    switch (item.type) {
      case AIFeatureType.PDF_EXTRACTION:
        setShowPDFModal(true);
        break;
      case AIFeatureType.SINGLE_LINKAGE:
        setShowLinkageModal(true);
        break;
      case AIFeatureType.ENGAGEMENT_RATING:
        setShowAssessmentModal(true);
        break;
      default:
        addToast(`${item.title} coming soon`, "info");
    }
  };

  return (
    <BackgroundContainer>
      <Column flex={showSideMenu ? "0 0 15%" : "0 0 0%"} show={showSideMenu}>
        <SideMenu show={showSideMenu} setShow={setShowSideMenu} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"compass-ai"} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <GradientTitle>{MESSAGES.COMPASS_AI.PAGE_TITLE}</GradientTitle>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={12} color={greyText}>
              {MESSAGES.COMPASS_AI.PAGE_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
        </NavBarContainer>

        <RowContainer ai={"flex-start"} jc={"flex-start"} wrap="wrap">
          {AI_FEATURE_ITEMS.map((item, index) => (
            <FeatureCard
              key={index}
              item={item}
              buttonText="Launch"
              onClick={(): void => handleFeatureClick(item)}
            />
          ))}
        </RowContainer>
      </Column>

      {/* AI Feature Modals */}
      <PDFExtractionModal
        show={showPDFModal}
        onHide={(): void => setShowPDFModal(false)}
        onSuccess={(result): void => {
          const count = result?.summary?.total_engagements_extracted ?? 0;
          addToast(`Successfully extracted ${count} engagements`, "success");
        }}
      />

      <LinkageAnalysisModal
        show={showLinkageModal}
        onHide={(): void => setShowLinkageModal(false)}
        onSuccess={(result): void => {
          addToast(
            `Found ${result.total_linkages_found} linkages across ${result.successful_companies} companies`,
            "success",
          );
        }}
      />

      <EngagementAssessmentModal
        show={showAssessmentModal}
        onHide={(): void => setShowAssessmentModal(false)}
        onSuccess={(result): void => {
          addToast(
            `Assessed ${result.total_records} engagements — ${result.successful} successful`,
            "success",
          );
        }}
      />
    </BackgroundContainer>
  );
};
